import React, { useState } from 'react';
import { calendarAPI, CalendarItem } from '@/services/calendarApi';
import { EVENT_TYPE_COLOR, EVENT_TYPE_LABELS } from '@/components/calendar/eventTypes';

interface PlayerEventDetailProps {
  item: CalendarItem;
  currentUserId?: string;
  onClose: () => void;
  onDeleted: () => void;
}

const fmtDateTime = (d: Date) =>
  d.toLocaleString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const fmtTime = (d: Date) =>
  d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

const PlayerEventDetail: React.FC<PlayerEventDetailProps> = ({
  item,
  currentUserId,
  onClose,
  onDeleted,
}) => {
  const [deleting, setDeleting] = useState(false);

  const start = new Date(item.start);
  const end = item.end ? new Date(item.end) : undefined;

  const canDelete =
    item.source === 'event' && !!item.createdBy && item.createdBy === currentUserId;

  const openAttachment = async (attachmentId: string) => {
    try {
      const blob = await calendarAPI.downloadAttachment(attachmentId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      window.alert('Could not open the attachment. Please try again.');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await calendarAPI.deletePersonal(item.id);
      onDeleted();
    } catch {
      window.alert('Could not delete the event. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-lg bg-white p-5 sm:p-6 shadow-xl dark:bg-gray-900 dark:text-gray-100 max-h-[92vh] overflow-y-auto">
        <div className="sm:hidden mx-auto mb-4 h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />

        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Event Details</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{item.title}</h2>
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium text-white ${
                EVENT_TYPE_COLOR[item.type] || 'bg-gray-500'
              }`}
            >
              {EVENT_TYPE_LABELS[item.type] || 'Other'}
            </span>
          </div>

          <div className="text-sm text-gray-700 dark:text-gray-300">
            <span>{fmtDateTime(start)}</span>
            {end && <span> – {fmtTime(end)}</span>}
          </div>

          {item.location && (
            <div className="text-sm text-gray-700 dark:text-gray-300">📍 {item.location}</div>
          )}

          {item.description && (
            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {item.description}
            </p>
          )}

          {item.attachments && item.attachments.length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                Attachments
              </h4>
              <div className="flex flex-wrap gap-2">
                {item.attachments.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => openAttachment(a.id)}
                    className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <span aria-hidden>📄</span>
                    {a.fileName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {canDelete && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="w-full sm:w-auto px-4 py-2 rounded-md bg-red-600 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete event'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerEventDetail;
